import React, { useState } from 'react';
import api from '../api';

export default function FileUploader({ taskId, projectId, existingIds = [], onUpdate }) {
  const [uploading, setUploading] = useState(false);
  const [files,     setFiles]     = useState([]);
  const [error,     setError]     = useState('');

  // Enforce: must have taskId or projectId
  const canUpload = !!(taskId || projectId);

  const handleFileChange = async (e) => {
    const selected = Array.from(e.target.files);
    if (!selected.length) return;
    if (!canUpload) {
      setError('Files must be linked to a task or project.');
      return;
    }

    setUploading(true);
    setError('');

    try {
      for (const file of selected) {
        const formData = new FormData();
        formData.append('file', file);
        if (taskId)    formData.append('task_id',    taskId);
        if (projectId) formData.append('project_id', projectId);

        // Upload file
        const uploadRes = await api.post('/wp/v2/media', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });

        const mediaId = uploadRes.data.id;

        // Link to task if taskId provided
        if (taskId) {
          const taskRes = await api.get(`/wp/v2/tasks/${taskId}`);
          const current = JSON.parse(taskRes.data.meta?.gpf_file_ids || '[]');
          await api.put(`/wp/v2/tasks/${taskId}`, {
            meta: { gpf_file_ids: JSON.stringify([...new Set([...current, mediaId])]) }
          });
        }

        // Link to project if projectId provided
        if (projectId) {
          const projRes = await api.get(`/wp/v2/projects/${projectId}`);
          const current = JSON.parse(projRes.data.meta?.gpf_file_ids || '[]');
          await api.put(`/wp/v2/projects/${projectId}`, {
            meta: { gpf_file_ids: JSON.stringify([...new Set([...current, mediaId])]) }
          });
        }

        setFiles(prev => [...prev, { id: mediaId, name: file.name, url: uploadRes.data.source_url }]);
      }
      if (onUpdate) onUpdate();
    } catch (err) {
      setError('Upload failed. Please try again.');
      console.error(err);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  // Fetch existing attached files
  const [existingFiles, setExistingFiles] = React.useState([]);
  React.useEffect(() => {
    if (!existingIds.length) { setExistingFiles([]); return; }
    Promise.all(existingIds.map(id => api.get(`/wp/v2/media/${id}`).catch(() => null)))
      .then(results => setExistingFiles(results.filter(Boolean).map(r => r.data)));
  }, [existingIds.join(',')]);

  const fileIcon = (mime = '') => {
    if (mime.startsWith('image/'))  return '🖼';
    if (mime.includes('pdf'))       return '📄';
    if (mime.includes('word'))      return '📝';
    if (mime.includes('sheet'))     return '📊';
    return '📎';
  };

  return (
    <div style={styles.wrap}>
      <div style={styles.header}>
        <span style={styles.title}>📎 Files</span>
        {canUpload && (
          <label style={{ ...styles.uploadBtn, opacity: uploading ? 0.6 : 1, cursor: uploading ? 'not-allowed' : 'pointer' }}>
            {uploading ? 'Uploading...' : '+ Attach File'}
            <input
              type="file"
              multiple
              style={{ display: 'none' }}
              onChange={handleFileChange}
              disabled={uploading}
            />
          </label>
        )}
      </div>

      {!canUpload && (
        <p style={styles.noContext}>Files must be linked to a task. Open a task to attach files.</p>
      )}

      {error && <p style={styles.error}>{error}</p>}

      {/* Existing files */}
      {existingFiles.length > 0 && (
        <div style={styles.fileList}>
          {existingFiles.map(file => (
            <a
              key={file.id}
              href={file.source_url}
              target="_blank"
              rel="noreferrer"
              style={styles.fileItem}
            >
              <span style={styles.fileIcon}>{fileIcon(file.mime_type)}</span>
              <span style={styles.fileName}>{file.title?.rendered || 'File'}</span>
              <span style={styles.fileSize}>
                {file.media_details?.filesize
                  ? `${(file.media_details.filesize / 1024).toFixed(0)} KB`
                  : ''}
              </span>
            </a>
          ))}
        </div>
      )}

      {/* Newly uploaded this session */}
      {files.map(f => (
        <a key={f.id} href={f.url} target="_blank" rel="noreferrer" style={styles.fileItem}>
          <span style={styles.fileIcon}>📎</span>
          <span style={styles.fileName}>{f.name}</span>
          <span style={{ ...styles.fileSize, color: '#16a34a' }}>✓ Uploaded</span>
        </a>
      ))}
    </div>
  );
}

const styles = {
  wrap:      { display: 'flex', flexDirection: 'column', gap: 8 },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  title:     { fontSize: 13, fontWeight: 700, color: '#374151' },
  uploadBtn: { fontSize: 12, fontWeight: 600, color: '#2563eb', background: '#eff6ff', padding: '4px 10px', borderRadius: 6, border: '1px solid #bfdbfe' },
  noContext: { fontSize: 12, color: '#94a3b8', fontStyle: 'italic', margin: 0 },
  error:     { fontSize: 12, color: '#dc2626', margin: 0 },
  fileList:  { display: 'flex', flexDirection: 'column', gap: 4 },
  fileItem:  { display: 'flex', alignItems: 'center', gap: 8, padding: '6px 8px', background: '#f8fafc', borderRadius: 6, textDecoration: 'none', border: '1px solid #f1f5f9' },
  fileIcon:  { fontSize: 14, flexShrink: 0 },
  fileName:  { fontSize: 12, color: '#374151', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  fileSize:  { fontSize: 11, color: '#94a3b8', flexShrink: 0 },
};